import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../../design-system/primitives/Button/Button.js';
import { Icon } from '@/components/icon/index.js';
import { getPermanentDeletionDate } from '../../../lib/deletion.js';

type DeletionPendingBannerProps = {
  scheduledAt: string;
  onCancel: () => void;
  canceling?: boolean;
  compact?: boolean;
};

export function DeletionPendingBanner({
  scheduledAt,
  onCancel,
  canceling = false,
  compact = false,
}: DeletionPendingBannerProps) {
  const { t, i18n } = useTranslation();
  const permanentDate = getPermanentDeletionDate(scheduledAt);
  const formatted = permanentDate.toLocaleDateString(i18n.language, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  if (compact) {
    return (
      <div className="border-b border-ig-danger/30 bg-ig-danger/10 px-6 py-3 md:px-8">
        <div className="mx-auto flex max-w-[1280px] flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="font-sans text-[13px] text-ig-text-primary">
            {t('settings.privacy.deletion.banner', { date: formatted })}
          </p>
          <div className="flex shrink-0 items-center gap-2">
            <Link
              to="/settings/privacy"
              className="font-sans text-[13px] text-ig-accent underline-offset-2 hover:underline"
            >
              {t('settings.privacy.deletion.manageLink')}
            </Link>
            <Button type="button" variant="primary" size="sm" loading={canceling} onClick={onCancel}>
              {t('settings.privacy.deletion.cancelButton')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-ig-danger/40 bg-ig-danger/10 p-5">
      <div className="flex items-start gap-3">
        <Icon name="info" className="mt-0.5 h-5 w-5 shrink-0 text-ig-danger" aria-hidden />
        <div className="min-w-0 flex-1 space-y-3">
          <div className="space-y-1">
            <p className="font-sans text-[15px] font-semibold text-ig-text-primary">
              {t('settings.privacy.deletion.pendingTitle')}
            </p>
            <p className="font-sans text-[14px] text-ig-text-secondary">
              {t('settings.privacy.deletion.pendingDescription', { date: formatted })}
            </p>
          </div>
          <Button type="button" variant="primary" size="md" loading={canceling} onClick={onCancel}>
            {t('settings.privacy.deletion.cancelButton')}
          </Button>
        </div>
      </div>
    </div>
  );
}
